import type {
  ID,
  Label,
  OrganizationIDs,
  PaginationInfo,
  PeriodIDs,
  Revision,
  Timestamps,
  UserID,
  Value,
} from './common'
import type { DefaultQueryParams } from './default-params'

export enum ComponentType {
  COMPONENT_TYPE_INTEGER = 'int',
  COMPONENT_TYPE_CURRENCY = 'currency',
  COMPONENT_TYPE_PERCENTAGE = 'percent',
  COMPONENT_TYPE_BOOL = 'bool',
  COMPONENT_TYPE_FLOAT = 'float',
}

export enum InputSourceType {
  INPUT_SOURCE_TYPE_USER = 'user',
  INPUT_SOURCE_TYPE_ORGANIZATION = 'organization',
}

export interface ComponentID {
  componentId: string
}

export interface Slug {
  slug: string
}

export enum ProrationMode {
  PRORATION_MODE_NONE = 'none',
  PRORATION_MODE_AMORTIZE = 'amortize',
  PRORATION_MODE_CUMULATIVE = 'cumulative',
  PRORATION_MODE_AVERAGE = 'average',
}

export enum CompositeFunction {
  COMPOSITE_FUNCTION_NONE = 'none',
  COMPOSITE_FUNCTION_AVERAGE = 'average',
  COMPOSITE_FUNCTION_MEDIAN = 'median',
  COMPOSITE_FUNCTION_MODE = 'mode',
  COMPOSITE_FUNCTION_COUNT = 'count',
  COMPOSITE_FUNCTION_STDDEV = 'stddev',
  COMPOSITE_FUNCTION_SUM = 'sum',
}

export enum ComponentSourceType {
  COMPONENT_SOURCE_MANUAL = 'manual',
  COMPONENT_SOURCE_COMPOSITE = 'composite',
}

export enum CompositeType {
  COMPOSITE_TYPE_SIMPLE = 'simple',
  COMPOSITE_TYPE_COMPLEX = 'complex',
}

export interface Component extends ID, OrganizationIDs, Label, Slug, Timestamps {
  ledgerAccountId: string | null
  isOrganizationLevel: boolean
  isTargetEditable: boolean
  restrictedVisibility: boolean
  targetMin: string
  targetMax: string
  sourceType: ComponentSourceType
  componentType: ComponentType
  sourceInfo?: {
    compositeFunction?: CompositeFunction
    compositeType?: CompositeType
  }
  lowerIsBetter: boolean
  cumulativeTargets: boolean
  numericDecimalPlaces?: number
  precision?: number
  isArchived: boolean
}

export interface TargetDTO extends OrganizationIDs, UserID, PeriodIDs, ComponentID, Label, Value, Slug {}

export interface CreateTargetDTO {
  organizationId: string
  rootOrganizationId: string
  periodId: string
  rootPeriodId: string
  label: string
  isOrganizationLevel: boolean
  isRestricted: boolean
  componentType: ComponentType
  targetMin: string
  targetMax: string
  value: string
  sourceType: ComponentSourceType
  sourceInfo: string
  lowerIsBetter?: boolean
  numericDecimalPlaces?: number
}

export interface CreateMetricDTO {
  componentId: string
  organizationId: string
  rootOrganizationId: string
  periodId: string
  rootPeriodId: string
  userId: string
  value: string
}

export interface Target extends TargetDTO, Slug, Revision, Pick<Timestamps, 'createdAt'> {
  componentType: ComponentType | null
  userFirstName?: string | null
  userLastName?: string | null
  isOrganizationLevel: boolean
}

export interface MetricDTO extends OrganizationIDs, PeriodIDs, UserID, ComponentID, Value {}

export interface Metric extends MetricDTO, Revision, Pick<Timestamps, 'createdAt'> {
  label: string | null
  componentType: ComponentType | null
  userFirstName?: string | null
  userLastName?: string | null
  isOrganizationLevel: boolean
  isVisible?: boolean
  effectiveDate?: string
}

export interface ExpandedTarget {
  target: Target
  component: Component
}

export interface ComponentSearchParams {
  periodId?: string[]
  organizationIds?: string[]
  userIds?: string[]
  searchTerm?: string
  perPage?: number
  page?: number
}

export interface ComponentQueryParams extends DefaultQueryParams {
  organizationIds: string[]
  periodIds: string[]
  userIds: string[]
  isArchived?: boolean
}

export interface ComponentSearchResponse {
  components: Component[]
  paginationInfo: PaginationInfo
}

export interface TargetQueryParams extends DefaultQueryParams {
  organizationIds: string[]
  periodIds: string[]
  isOrganizationLevel?: boolean
  isArchived?: boolean
}

export interface TargetSearchResponse {
  targets: Target[]
  paginationInfo: PaginationInfo
}

export interface MetricQueryParams extends DefaultQueryParams {
  organizationIds: string[]
  periodIds: string[]
  isVisible?: boolean
  isOrganizationLevel?: boolean
  isArchived?: boolean
}

export interface ExpandedTargetSearchResponse {
  targets: ExpandedTarget[]
  paginationInfo: PaginationInfo
}

export interface MetricSearchResponse {
  metrics: Metric[]
  paginationInfo: PaginationInfo
}

export function formatValue(
  value: string | number,
  componentType: ComponentType,
  includeCurrencySymbol: boolean = true,
  numericDecimalPlaces?: number
): string {
  const parsedValue = typeof value === 'string' ? Number.parseFloat(value) : value

  switch (componentType) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
      // Use numericDecimalPlaces if provided, otherwise default to 0 for integers
      const intDecimalPlaces = numericDecimalPlaces !== undefined ? numericDecimalPlaces : 0
      return parsedValue.toLocaleString(undefined, {
        minimumFractionDigits: intDecimalPlaces,
        maximumFractionDigits: intDecimalPlaces,
      })
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      if (!includeCurrencySymbol) {
        return parsedValue.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      }
      return parsedValue.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return `${parsedValue.toFixed(2)}%`
    case ComponentType.COMPONENT_TYPE_FLOAT:
      // Use numericDecimalPlaces if provided, otherwise default to 2 for floats
      const floatDecimalPlaces = numericDecimalPlaces !== undefined ? numericDecimalPlaces : 2
      return parsedValue.toFixed(floatDecimalPlaces)
    case ComponentType.COMPONENT_TYPE_BOOL:
      return value.toLocaleString().toLocaleLowerCase() === 'true' ? 'True' : 'False'
    default:
      return value.toString()
  }
}

export function unformatValue(formattedValue: string, componentType: ComponentType): string | number {
  switch (componentType) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return formattedValue.replace(/[$,]/g, '')
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return Number.parseFloat(formattedValue.replace('%', ''))
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return Number.parseFloat(formattedValue)
    case ComponentType.COMPONENT_TYPE_BOOL:
      return formattedValue.toLocaleLowerCase() === 'yes' || formattedValue.toLocaleLowerCase() === 'true'
        ? 'true'
        : 'false'
    default:
      return formattedValue
  }
}

export function getTargetCompositeId(target: Target): string {
  return `${target.componentId}_${target.periodId}_${target.userId || ''}`
}

export function getMetricCompositeId(metric: Metric | any): string {
  return `${metric.componentId}_${metric.organizationId}_${metric.periodId}_${metric.userId}`
}

export interface NeededTargetMetric {
  componentId: string
  rootOrganizationId: string
  organizationId: string
  rootPeriodId: string
  componentType: ComponentType
  periodId: string
  userId: string
  label: string
  value: string
  firstName: string
  lastName: string
  newValue?: string
  currentActualValue?: string
  sourceType?: ComponentSourceType
  sourceInfo?: SourceInfo
  lowerIsBetter?: boolean
  numericDecimalPlaces?: number
}

export interface SourceInfo {
  compositeFunction?: CompositeFunction
  compositeType?: CompositeType
}

export interface SearchNeededTargetMetricsParams {
  organizationIds: string[]
  periodIds: string[]
  excludeMetricsEntered?: boolean
  limit?: number
}

export interface TargetDetailsParams {
  componentId: string
  organizationId: string
  periodId: string
  userId?: string | null
  revision?: number
}

export interface MetricDetailsParams {
  componentId: string
  organizationId: string
  periodId: string
  userId?: string | null
}

interface TargetRevision {
  componentId: string
  rootOrganizationId: string
  organizationId: string
  rootPeriodId: string
  periodId: string
  userId: string | null
  label: string
  slug: string
  revision: number
  value: string
  createdAt: string
  approvalBatchId: string | null
  approvalStatus: string
}

interface TargetComponent {
  id: string
  rootOrganizationId: string
  organizationId: string
  ledgerAccountId: string | null
  label: string
  isOrganizationLevel: boolean
  isTargetEditable: boolean
  restrictedVisibility: boolean
  slug: string
  targetMin: string
  targetMax: string
  format: string
  sourceType: string
  componentType: string
  numericDecimalPlaces?: number
  lowerIsBetter?: boolean
  createdAt: string
  updatedAt: string
  sourceInfo?: SourceInfo
}

interface TemplateInfo {
  id: string
  revision: number
  label: string
  periodId: string
  rootPeriodId: string
  periodLabel: string
}

interface PlanInfo {
  id: string
  revision: number
  userId: string
  firstName: string
  lastName: string
  approvalBatchId: string | null
  approvalStatus: string
  periodId: string
  rootPeriodId: string
  periodLabel: string
}

export interface TargetDetailsResponse {
  revisions: TargetRevision[]
  component: TargetComponent
  templates: TemplateInfo[]
  plans: PlanInfo[]
  organization: {
    id: string
    name: string
    parentId: string | null
    rootOrganizationId: string
    createdAt: string
    updatedAt: string
  }
  period: {
    id: string
    parentId: string | null
    organizationId: string
    label: string
    startDate: string
    endDate: string
    payDate: string | null
    isClosed?: boolean
    createdAt: string
    updatedAt: string
  }
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    startDate: string
    endDate: string | null
    position: string
    metadata: Record<string, unknown>
    ledgerAccountId: string | null
    createdAt: string
    updatedAt: string
  } | null
  coreSettings: {
    organizationId: string
    defaultLedgerAccount: string
    currency: string
    currencyDecimals: number
    defaultBasePlus: boolean
    defaultExampleCompensation: number
    defaultVariableCompensation: string
    defaultMaxPayout: string
    defaultTemplateTables: string
    targetApprovalChain: string
    metricApprovalChain: string
    planApprovalChain: string
    accrualApprovalChain: string
  }
}

export interface MetricDetailsResponse {
  revisions: MetricRevision[]
  component: TargetComponent
  templates: TemplateInfo[]
  plans: PlanInfo[]
  latestTarget: TargetRevision | null
  organization: {
    id: string
    name: string
    parentId: string | null
    rootOrganizationId: string
    createdAt: string
    updatedAt: string
  }
  period: {
    id: string
    parentId: string | null
    organizationId: string
    label: string
    startDate: string
    endDate: string
    payDate: string | null
    isClosed?: boolean
    createdAt: string
    updatedAt: string
  }
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    startDate: string
    endDate: string | null
    position: string
    metadata: Record<string, unknown>
    ledgerAccountId: string | null
    createdAt: string
    updatedAt: string
  } | null
  coreSettings: {
    organizationId: string
    defaultLedgerAccount: string
    currency: string
    currencyDecimals: number
    defaultBasePlus: boolean
    defaultExampleCompensation: number
    defaultVariableCompensation: string
    defaultMaxPayout: string
    defaultTemplateTables: string
    targetApprovalChain: string
    metricApprovalChain: string
    planApprovalChain: string
    accrualApprovalChain: string
  }
}

export interface MetricRevision {
  componentId: string
  rootOrganizationId: string
  organizationId: string
  rootPeriodId: string
  periodId: string
  userId: string
  revision: number
  value: string
  approvalBatchId: string
  createdAt: string
  approvalStatus: string
}

export function getInputFormatFromComponentType(componentType: ComponentType): ComponentType {
  switch (componentType) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
      return ComponentType.COMPONENT_TYPE_INTEGER
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return ComponentType.COMPONENT_TYPE_CURRENCY
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return ComponentType.COMPONENT_TYPE_PERCENTAGE
    case ComponentType.COMPONENT_TYPE_BOOL:
      return ComponentType.COMPONENT_TYPE_BOOL
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return ComponentType.COMPONENT_TYPE_FLOAT
    default:
      throw new Error(`Unknown component type: ${componentType}`)
  }
}

export interface LabelResponse {
  labels: string[]
}

export interface ComponentDetails {
  component: Component
  approvedTargets: number
  approvedMetrics: number
  templates: TemplateInfo[]
  plans: PlanInfo[]
}

export function getCompositeFunction(compositeFunctionStr?: string | object): CompositeFunction {
  if (!compositeFunctionStr) {
    return CompositeFunction.COMPOSITE_FUNCTION_NONE
  }

  if (typeof compositeFunctionStr === 'object') {
    return normalizeCompositeFunctionValue((compositeFunctionStr as any).compositeFunction)
  }

  try {
    const parsed = JSON.parse(compositeFunctionStr)
    if (parsed && typeof parsed === 'object') {
      return normalizeCompositeFunctionValue((parsed as any).compositeFunction)
    }
  } catch {
    return normalizeCompositeFunctionValue(compositeFunctionStr)
  }

  return CompositeFunction.COMPOSITE_FUNCTION_NONE
}

export interface MetricReductionResult {
  functionUsed: CompositeFunction
  rawValue: string
  reducedValue: number
  diagnostics?: CompositeReductionDiagnostics
}

export interface CompositeActualValueItem {
  label: string
  description: string
  value: string
  target?: string
  componentType?: ComponentType
  targetComponentType?: ComponentType
}

export interface CompositeActualValue {
  values: CompositeActualValueItem[]
  // Future fields can be added here
}

export interface CompositeTargetValueItem {
  label: string
  description: string
  target: string
  componentType: ComponentType
}

export interface CompositeTargetValue {
  value: string
  compositeTargetValues?: CompositeTargetValueItem[]
}

const TRUTHY_BOOLEAN_VALUES = new Set(['true', '1', '1.0', 't', 'y', 'yes'])
const FALSY_BOOLEAN_VALUES = new Set(['false', '0', '0.0', 'f', 'n', 'no'])

const normalizeCompositeFunctionValue = (functionValue?: string | CompositeFunction | null): CompositeFunction => {
  if (!functionValue) {
    return CompositeFunction.COMPOSITE_FUNCTION_NONE
  }

  switch (functionValue.toString().trim().toLowerCase()) {
    case CompositeFunction.COMPOSITE_FUNCTION_AVERAGE:
      return CompositeFunction.COMPOSITE_FUNCTION_AVERAGE
    case CompositeFunction.COMPOSITE_FUNCTION_MEDIAN:
      return CompositeFunction.COMPOSITE_FUNCTION_MEDIAN
    case CompositeFunction.COMPOSITE_FUNCTION_MODE:
      return CompositeFunction.COMPOSITE_FUNCTION_MODE
    case CompositeFunction.COMPOSITE_FUNCTION_COUNT:
      return CompositeFunction.COMPOSITE_FUNCTION_COUNT
    case CompositeFunction.COMPOSITE_FUNCTION_STDDEV:
    case 'stdev':
      return CompositeFunction.COMPOSITE_FUNCTION_STDDEV
    case CompositeFunction.COMPOSITE_FUNCTION_SUM:
      return CompositeFunction.COMPOSITE_FUNCTION_SUM
    default:
      return CompositeFunction.COMPOSITE_FUNCTION_NONE
  }
}

export function normalizeCompositeTypeValue(
  compositeType?: CompositeType | string | null,
  fallback: CompositeType = CompositeType.COMPOSITE_TYPE_SIMPLE
): CompositeType {
  if (!compositeType) {
    return fallback
  }

  const normalizedType = compositeType.toString().trim().toLowerCase()
  if (normalizedType === CompositeType.COMPOSITE_TYPE_COMPLEX) {
    return CompositeType.COMPOSITE_TYPE_COMPLEX
  }
  if (normalizedType === CompositeType.COMPOSITE_TYPE_SIMPLE) {
    return CompositeType.COMPOSITE_TYPE_SIMPLE
  }

  return fallback
}

export function normalizeComponentType(
  componentType?: ComponentType | string | null,
  fallback: ComponentType = ComponentType.COMPONENT_TYPE_FLOAT
): ComponentType {
  if (!componentType) {
    return fallback
  }

  const normalizedType = componentType.toString().trim().toLowerCase()
  switch (normalizedType) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
    case 'integer':
      return ComponentType.COMPONENT_TYPE_INTEGER
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return ComponentType.COMPONENT_TYPE_CURRENCY
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
    case 'percentage':
      return ComponentType.COMPONENT_TYPE_PERCENTAGE
    case ComponentType.COMPONENT_TYPE_BOOL:
    case 'boolean':
      return ComponentType.COMPONENT_TYPE_BOOL
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return ComponentType.COMPONENT_TYPE_FLOAT
    default:
      return fallback
  }
}

export function parseValueByComponentType(value: unknown, componentType: ComponentType): number | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null
    }

    if (componentType === ComponentType.COMPONENT_TYPE_BOOL) {
      return value === 0 ? 0 : 1
    }

    return value
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0
  }

  const stringValue = value.toString().trim()
  if (!stringValue) {
    return null
  }

  if (componentType === ComponentType.COMPONENT_TYPE_BOOL) {
    const normalizedBooleanValue = stringValue.toLowerCase()
    if (TRUTHY_BOOLEAN_VALUES.has(normalizedBooleanValue)) {
      return 1
    }
    if (FALSY_BOOLEAN_VALUES.has(normalizedBooleanValue)) {
      return 0
    }
    return null
  }

  const normalizedNumericValue = stringValue.replace(/[$,%\s,]/g, '')
  if (!normalizedNumericValue) {
    return null
  }

  const parsedNumericValue = Number.parseFloat(normalizedNumericValue)
  if (!Number.isFinite(parsedNumericValue)) {
    return null
  }

  return parsedNumericValue
}

export function parseCompositeActualValue(
  value: string | CompositeActualValue | null | undefined
): CompositeActualValue {
  if (!value) {
    return { values: [] }
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as CompositeActualValue
      if (!parsed || !Array.isArray(parsed.values)) {
        return { values: [] }
      }
      return parsed
    } catch {
      return { values: [] }
    }
  }

  if (!Array.isArray(value.values)) {
    return { values: [] }
  }

  return value
}

export function parseCompositeTargetValue(
  value: string | CompositeTargetValue | null | undefined
): CompositeTargetValue | null {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as CompositeTargetValue
      if (!parsed || typeof parsed !== 'object') {
        return null
      }
      return parsed
    } catch {
      return null
    }
  }

  return value
}

const getCompositeLookupKey = (label: string | undefined, description: string | undefined): string => {
  const normalizedLabel = (label || '').trim().toLowerCase()
  const normalizedDescription = (description || '').trim().toLowerCase()
  return `${normalizedLabel}::${normalizedDescription}`
}

export interface CompositeReductionDiagnostics {
  totalRows: number
  usedRows: number
  skippedRows: number
  parseErrors: number
  typeMismatches: number
  zeroDenominatorRows: number
}

export interface CompositeValueOptions {
  compositeFunction?: CompositeFunction | string
  compositeType?: CompositeType | string
  fallbackComponentType?: ComponentType | string
  lowerIsBetter?: boolean
}

export interface CompositeValueResult {
  functionUsed: CompositeFunction
  reducedValue: number
  values: number[]
  diagnostics: CompositeReductionDiagnostics
}

const applyCompositeFunctionToValues = (compositeFunction: CompositeFunction, values: number[]): number => {
  if (values.length === 0) {
    return 0
  }

  switch (compositeFunction) {
    case CompositeFunction.COMPOSITE_FUNCTION_SUM:
      return values.reduce((accumulator, current) => accumulator + current, 0)
    case CompositeFunction.COMPOSITE_FUNCTION_AVERAGE:
      return values.reduce((accumulator, current) => accumulator + current, 0) / values.length
    case CompositeFunction.COMPOSITE_FUNCTION_MEDIAN: {
      const sortedValues = [...values].sort((first, second) => first - second)
      const midPoint = Math.floor(sortedValues.length / 2)
      if (sortedValues.length % 2 === 0) {
        return (sortedValues[midPoint - 1] + sortedValues[midPoint]) / 2
      }
      return sortedValues[midPoint]
    }
    case CompositeFunction.COMPOSITE_FUNCTION_MODE: {
      const countsByValue = new Map<number, { count: number; firstIndex: number }>()
      values.forEach((value, index) => {
        const existing = countsByValue.get(value)
        if (existing) {
          existing.count += 1
          return
        }

        countsByValue.set(value, { count: 1, firstIndex: index })
      })

      let modeValue = values[0]
      let highestCount = 0
      let bestIndex = Number.POSITIVE_INFINITY

      for (const [value, metadata] of countsByValue.entries()) {
        if (metadata.count > highestCount) {
          highestCount = metadata.count
          bestIndex = metadata.firstIndex
          modeValue = value
          continue
        }

        if (metadata.count === highestCount && metadata.firstIndex < bestIndex) {
          bestIndex = metadata.firstIndex
          modeValue = value
        }
      }

      return modeValue
    }
    case CompositeFunction.COMPOSITE_FUNCTION_STDDEV: {
      const meanValue = values.reduce((accumulator, current) => accumulator + current, 0) / values.length
      const squaredDiffSum = values.reduce((accumulator, current) => accumulator + Math.pow(current - meanValue, 2), 0)
      const variance = squaredDiffSum / values.length
      return Math.sqrt(variance)
    }
    case CompositeFunction.COMPOSITE_FUNCTION_COUNT:
      return values.length
    default:
      return 0
  }
}

export function evaluateCompositeMetricValue(
  metricValue: string | CompositeActualValue,
  options: CompositeValueOptions
): CompositeValueResult {
  const functionUsed = normalizeCompositeFunctionValue(options.compositeFunction)
  const normalizedCompositeType = normalizeCompositeTypeValue(options.compositeType)
  const fallbackComponentType = normalizeComponentType(options.fallbackComponentType)
  const lowerIsBetter = options.lowerIsBetter || false

  const diagnostics: CompositeReductionDiagnostics = {
    totalRows: 0,
    usedRows: 0,
    skippedRows: 0,
    parseErrors: 0,
    typeMismatches: 0,
    zeroDenominatorRows: 0,
  }

  const parsedMetric = parseCompositeActualValue(metricValue)
  const parsedValues: number[] = []

  for (const row of parsedMetric.values) {
    diagnostics.totalRows += 1

    const rowComponentType = normalizeComponentType(row.componentType, fallbackComponentType)
    const parsedActualValue = parseValueByComponentType(row.value, rowComponentType)
    if (parsedActualValue === null) {
      diagnostics.parseErrors += 1
      continue
    }

    let reducedRowValue = parsedActualValue

    if (
      normalizedCompositeType === CompositeType.COMPOSITE_TYPE_COMPLEX &&
      row.target !== undefined &&
      row.target !== ''
    ) {
      const rowTargetType = normalizeComponentType(row.targetComponentType || row.componentType, rowComponentType)
      if (row.targetComponentType && rowTargetType !== rowComponentType) {
        diagnostics.typeMismatches += 1
        continue
      }

      const parsedTargetValue = parseValueByComponentType(row.target, rowTargetType)
      if (parsedTargetValue === null) {
        diagnostics.parseErrors += 1
        continue
      }

      if (!lowerIsBetter) {
        if (parsedTargetValue === 0) {
          diagnostics.zeroDenominatorRows += 1
          reducedRowValue = 0
        } else {
          reducedRowValue = parsedActualValue / parsedTargetValue
        }
      } else {
        if (parsedActualValue === 0) {
          diagnostics.zeroDenominatorRows += 1
          reducedRowValue = 0
        } else {
          reducedRowValue = parsedTargetValue / parsedActualValue
        }
      }
    }

    if (!Number.isFinite(reducedRowValue)) {
      diagnostics.parseErrors += 1
      continue
    }

    parsedValues.push(reducedRowValue)
  }

  diagnostics.usedRows = parsedValues.length
  diagnostics.skippedRows = diagnostics.totalRows - diagnostics.usedRows

  const reducedValue = applyCompositeFunctionToValues(functionUsed, parsedValues)

  return {
    functionUsed,
    reducedValue: Number.isFinite(reducedValue) ? reducedValue : 0,
    values: parsedValues,
    diagnostics,
  }
}

/**
 * Returns the overall target value for a composite target.
 * The composite function is NOT applied to target rows -- it is only applied to actuals/metrics.
 * For simple composites, target rows are display-only; the overall value is what matters.
 * For complex composites, per-row targets are used during metric evaluation (metric/target ratios),
 * but the overall target for progress comparison is the manually-set value field.
 */
export function evaluateCompositeTargetValue(
  targetValue: string | CompositeTargetValue | null | undefined,
  options: CompositeValueOptions
): CompositeValueResult {
  const functionUsed = normalizeCompositeFunctionValue(options.compositeFunction)
  const fallbackComponentType = normalizeComponentType(options.fallbackComponentType)

  const diagnostics: CompositeReductionDiagnostics = {
    totalRows: 0,
    usedRows: 0,
    skippedRows: 0,
    parseErrors: 0,
    typeMismatches: 0,
    zeroDenominatorRows: 0,
  }

  const parsedTarget = parseCompositeTargetValue(targetValue)
  if (!parsedTarget) {
    return {
      functionUsed,
      reducedValue: 0,
      values: [],
      diagnostics,
    }
  }

  const parsedOverallTargetValue = parseValueByComponentType(parsedTarget.value, fallbackComponentType)

  if (parsedOverallTargetValue !== null) {
    diagnostics.totalRows = 1
    diagnostics.usedRows = 1
    return {
      functionUsed,
      reducedValue: parsedOverallTargetValue,
      values: [parsedOverallTargetValue],
      diagnostics,
    }
  }

  return {
    functionUsed,
    reducedValue: 0,
    values: [],
    diagnostics,
  }
}

/**
 * Merges composite target structure into metric composite structure
 * If the target has composite structure, the target values will be merged into the metric values
 * @param metricValue - The metric value (either string JSON or CompositeActualValue)
 * @param targetValue - The target value (either string JSON or CompositeTargetValue)
 * @param fallbackComponentType - The fallback component type if not specified in items
 * @returns A CompositeActualValue with target information merged in
 *
 * @example
 * // With composite target structure
 * const metricValue = '{"values":[{"label":"Sales","description":"","value":"100","target":"","componentType":"int"}]}'
 * const targetValue = '{"value":"150","compositeTargetValues":[{"label":"Sales","description":"Q1 Sales","target":"120","componentType":"int"}]}'
 * const result = mergeCompositeTargetIntoMetric(metricValue, targetValue, ComponentType.COMPONENT_TYPE_INTEGER)
 * // Returns: {"values":[{"label":"Sales","description":"Q1 Sales","value":"100","target":"120","componentType":"int"}]}
 *
 * @example
 * // Without composite target structure
 * const metricValue = '{"values":[{"label":"Sales","description":"","value":"100"}]}'
 * const targetValue = '{"value":"150"}' // No compositeTargetValues
 * const result = mergeCompositeTargetIntoMetric(metricValue, targetValue, ComponentType.COMPONENT_TYPE_INTEGER)
 * // Returns: {"values":[{"label":"Sales","description":"","value":"100"}]} (unchanged)
 */
export function mergeCompositeTargetIntoMetric(
  metricValue: string | CompositeActualValue,
  targetValue: string | CompositeTargetValue | null | undefined,
  fallbackComponentType: ComponentType
): CompositeActualValue {
  const parsedMetric = parseCompositeActualValue(metricValue)
  const parsedTarget = parseCompositeTargetValue(targetValue)

  if (!parsedTarget || !parsedTarget.compositeTargetValues || parsedTarget.compositeTargetValues.length === 0) {
    return parsedMetric
  }

  const metricRows = parsedMetric.values || []
  const availableMetricIndexes = new Set(metricRows.map((_row, index) => index))
  const metricIndexesByKey = new Map<string, number[]>()

  metricRows.forEach((row, index) => {
    const key = getCompositeLookupKey(row.label, row.description)
    const existingIndexes = metricIndexesByKey.get(key) || []
    existingIndexes.push(index)
    metricIndexesByKey.set(key, existingIndexes)
  })

  const takeIndexByKey = (key: string): number | null => {
    const indexes = metricIndexesByKey.get(key)
    if (!indexes || indexes.length === 0) {
      return null
    }

    while (indexes.length > 0) {
      const candidateIndex = indexes.shift() as number
      if (availableMetricIndexes.has(candidateIndex)) {
        availableMetricIndexes.delete(candidateIndex)
        return candidateIndex
      }
    }

    return null
  }

  const takeIndexByPosition = (index: number): number | null => {
    if (availableMetricIndexes.has(index)) {
      availableMetricIndexes.delete(index)
      return index
    }

    const nextAvailableIndex = [...availableMetricIndexes].sort((first, second) => first - second)[0]
    if (nextAvailableIndex === undefined) {
      return null
    }

    availableMetricIndexes.delete(nextAvailableIndex)
    return nextAvailableIndex
  }

  const mergedValues: CompositeActualValueItem[] = parsedTarget.compositeTargetValues.map((targetItem, index) => {
    const targetKey = getCompositeLookupKey(targetItem.label, targetItem.description)
    const keyedMetricIndex = takeIndexByKey(targetKey)
    const metricIndex = keyedMetricIndex !== null ? keyedMetricIndex : takeIndexByPosition(index)

    const existingActualItem = metricIndex !== null ? metricRows[metricIndex] : undefined
    const targetComponentType = normalizeComponentType(targetItem.componentType, fallbackComponentType)
    const resolvedMetricComponentType = existingActualItem?.componentType
      ? normalizeComponentType(existingActualItem.componentType, fallbackComponentType)
      : targetComponentType

    return {
      label: targetItem.label || existingActualItem?.label || '',
      description: targetItem.description || existingActualItem?.description || '',
      value: existingActualItem?.value || '0',
      target: targetItem.target,
      componentType: resolvedMetricComponentType,
      targetComponentType,
    }
  })

  return {
    values: mergedValues,
  }
}

export function reduceMetricValue(
  metric: Metric,
  component: Component,
  compositeType: CompositeType = CompositeType.COMPOSITE_TYPE_SIMPLE
): MetricReductionResult {
  let functionUsed = CompositeFunction.COMPOSITE_FUNCTION_NONE
  const rawValue = metric.value
  let reducedValue = 0
  let diagnostics: CompositeReductionDiagnostics | undefined

  if (component.sourceType == ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
    functionUsed = getCompositeFunction(component.sourceInfo)

    const evaluation = evaluateCompositeMetricValue(metric.value, {
      compositeFunction: functionUsed,
      compositeType,
      fallbackComponentType: component.componentType,
      lowerIsBetter: component.lowerIsBetter,
    })

    reducedValue = evaluation.reducedValue
    diagnostics = evaluation.diagnostics
  } else {
    const parsedValue = parseValueByComponentType(rawValue, normalizeComponentType(component.componentType))
    reducedValue = parsedValue !== null ? parsedValue : 0
  }

  return {
    functionUsed,
    rawValue,
    reducedValue,
    diagnostics,
  }
}
